import * as XLSX from 'xlsx';

const PHONE_HEADER_HINTS = ['телефон', 'phone', 'номер', 'mobile', 'whatsapp', 'тел', 'msisdn'];

export function normalizePhone(value) {
    return String(value ?? '').replace(/\D/g, '');
}

export function parseRecipientsText(text, variableCount) {
    const lines = text
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);

    return lines.map((line, index) => {
        const parts = line.split(/[;,]/).map((p) => p.trim());
        const phone = normalizePhone(parts[0]);

        if (!phone) {
            throw new Error(`Строка ${index + 1}: не указан номер телефона`);
        }

        const variables = parts.slice(1);
        if (variables.length !== variableCount) {
            throw new Error(
                `Строка ${index + 1}: нужно ${variableCount} переменных (формат: номер;знач1;знач2)`
            );
        }

        return { phone, variables };
    });
}

export function readSpreadsheetFile(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (event) => {
            try {
                const data = new Uint8Array(event.target.result);
                const workbook = XLSX.read(data, { type: 'array', cellDates: false });
                const sheetName = workbook.SheetNames[0];

                if (!sheetName) {
                    reject(new Error('Файл пустой'));
                    return;
                }

                const sheet = workbook.Sheets[sheetName];
                const rows = XLSX.utils.sheet_to_json(sheet, {
                    header: 1,
                    defval: '',
                    raw: false,
                });

                const nonEmptyRows = rows.filter((row) =>
                    Array.isArray(row) && row.some((cell) => String(cell).trim())
                );

                if (nonEmptyRows.length < 2) {
                    reject(new Error('В файле должна быть строка заголовков и хотя бы одна строка с данными'));
                    return;
                }

                const headers = nonEmptyRows[0].map((cell, index) => {
                    const label = String(cell).trim();
                    return label || `Колонка ${index + 1}`;
                });

                const dataRows = nonEmptyRows.slice(1).map((row) =>
                    headers.map((_, index) => String(row[index] ?? '').trim())
                );

                resolve({ headers, rows: dataRows, sheetName });
            } catch (err) {
                reject(new Error('Не удалось прочитать файл. Поддерживаются .xlsx, .xls, .csv'));
            }
        };

        reader.onerror = () => reject(new Error('Ошибка чтения файла'));
        reader.readAsArrayBuffer(file);
    });
}

export function guessPhoneColumnIndex(headers) {
    const index = headers.findIndex((header) => {
        const value = header.toLowerCase();
        return PHONE_HEADER_HINTS.some((hint) => value.includes(hint));
    });

    return index >= 0 ? index : 0;
}

export function guessVariableColumnIndices(headers, phoneColumnIndex, variableCount) {
    const indices = [];

    for (let i = 0; i < headers.length && indices.length < variableCount; i += 1) {
        if (i === phoneColumnIndex) continue;

        const header = headers[i].toLowerCase();
        if (
            header.includes('перем') ||
            header.includes('var') ||
            header.includes('param') ||
            /^\{\{?\d+\}?\}$/.test(header.trim())
        ) {
            indices.push(i);
        }
    }

    if (indices.length === variableCount) {
        return indices;
    }

    const fallback = [];
    for (let i = 0; i < headers.length && fallback.length < variableCount; i += 1) {
        if (i !== phoneColumnIndex) {
            fallback.push(i);
        }
    }

    return fallback.slice(0, variableCount);
}

export function buildRecipientsFromSheet(rows, phoneColumnIndex, variableColumnIndices, variableCount) {
    if (phoneColumnIndex === null || phoneColumnIndex === undefined || phoneColumnIndex < 0) {
        throw new Error('Выберите колонку с телефоном');
    }

    if (variableCount > 0 && variableColumnIndices.length !== variableCount) {
        throw new Error(`Выберите ${variableCount} колонок для переменных шаблона`);
    }

    const recipients = [];
    const errors = [];

    rows.forEach((row, index) => {
        const phone = normalizePhone(row[phoneColumnIndex]);
        if (!phone) {
            errors.push(`Строка ${index + 2}: пустой номер`);
            return;
        }

        const variables = variableColumnIndices.map((colIndex) => String(row[colIndex] ?? '').trim());

        if (variables.some((value) => !value) && variableCount > 0) {
            errors.push(`Строка ${index + 2}: не заполнены все переменные`);
            return;
        }

        recipients.push({ phone, variables });
    });

    if (recipients.length === 0) {
        throw new Error(errors[0] || 'В файле нет валидных получателей');
    }

    if (errors.length > 0) {
        throw new Error(`${errors[0]} (всего проблемных строк: ${errors.length})`);
    }

    return recipients;
}

export function downloadRecipientsTemplate(variableCount) {
    const headers = [
        'Телефон',
        ...Array.from({ length: variableCount }, (_, i) => `Переменная ${i + 1}`),
    ];

    const sampleRow = [
        '996555123456',
        ...Array.from({ length: variableCount }, (_, i) => `значение ${i + 1}`),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet([headers, sampleRow]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Получатели');
    XLSX.writeFile(workbook, 'wave_recipients_template.xlsx');
}
